import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useAtomValue } from 'jotai';

import { Theme, ArrowRightSLine } from '@bizzabo/design-system';
import { useUserByProfileId } from '@bizzabo/entities';

import FirstPage from './components/FirstPage';
import SecondPage from './components/SecondPage';
import useCreateMeeting from '../hooks/useCreateMeeting';
import { LocationType } from '../types';
import useScheduledMeetingTimes from '../hooks/useScheduledMeetingTimes';
import { useTimesSelectionArray } from './hooks/useTimesSelectionArray';
import useTypeTracking from '../../../analytics';
import SchedulingOverlayWrapper from '../SchedulingOverlayWrapper';
import { useMeetings, View } from '../MeetingsTool/MeetingsToolContext';
import LoaderButton from '../components/LoaderButton';
import ZeroAvailableTimes from './components/ZeroAvailableTimes';
import WizardLoader from './components/WizardLoader';
import { groupByZonedDayToArray } from '../helpers';
import { FOLD_TIMES_AFTER } from '../constants';
import currentTimeZoneIdAtom from '../timeZoneId';

export enum WizardPage {
    First,
    Second,
}

const DEFAULT_INVITATION_MESSAGE =
    "Hi, I'd like to schedule a meeting with you. I've included my availability; please pick a time that works for you. Thanks!";

const LIMIT_OF_TIMES_TO_SEND = 100;
const SchedulingWizard: React.FC = () => {
    const { setView, eventId, schedulingWizardArgs } = useMeetings();
    const [locationType, setLocationType] = useState(LocationType.Physical);
    const [physicalLocationText, setPhysicalLocationText] = useState<string>('');
    const [introMessage, setIntroMessage] = useState(DEFAULT_INVITATION_MESSAGE);
    const [selectedTimes, dispatchSelectedTime] = useTimesSelectionArray();
    const [page, setPage] = useState<WizardPage>(WizardPage.First);
    const [isTimesNotFoundError, setIsTimesNotFoundError] = useState<boolean>(false);
    const createMeeting = useCreateMeeting();
    const classes = useStyles();
    const otherAttendeeId = schedulingWizardArgs.otherAttendeeId;

    const { data: otherAttendee, isSuccess: isSuccessOtherAttendee } = useUserByProfileId({
        eventId,
        userProfileId: otherAttendeeId,
    });

    const { trackClickEvent, trackSystemEvent, Track } = useTypeTracking({
        data: {
            description: {
                receiver_id: otherAttendee?.userProfileId,
                location: 'MEETING_WIZARD',
                flow: 'CREATE_MEETING',
                meeting_type: 'SCHEDULED',
            },
        },
    });

    const {
        isLoading: isLoadingTimes,
        data: timesResponse,
        isSuccess,
        refetch,
    } = useScheduledMeetingTimes(eventId, [otherAttendeeId]);
    const timesLoaded = !isLoadingTimes && isSuccess;

    const nameToDisplay = otherAttendee?.firstName || '';

    const timeZoneId = useAtomValue(currentTimeZoneIdAtom);

    useEffect(() => {
        if (timesLoaded && isSuccessOtherAttendee) {
            trackSystemEvent({
                description: {
                    action: 'MEETING_TIMES_LOADED',
                    is_received_times: !!timesResponse.times.length,
                    meeting_participant_role: 'HOST',
                },
            });
        }
    }, [timesLoaded, isSuccessOtherAttendee]);

    // auto-select first ${FOLD_TIMES_AFTER} times displayed on each day
    useEffect(() => {
        const times = timesLoaded ? timesResponse.times : [];
        const groupedTimes = groupByZonedDayToArray(times, timeZoneId);
        const initiallyShownTimes = groupedTimes.flatMap(timesGroup =>
            timesGroup.values.slice(0, FOLD_TIMES_AFTER)
        );
        dispatchSelectedTime({ type: 'reset', times: initiallyShownTimes });
    }, [timesLoaded]);

    const onRefetch = () => {
        setPage(WizardPage.First);
        refetch();
        trackClickEvent({ subCategory: 'RETRY_SCHEDULE_MEETING' });
    };

    const onClickForward = async () => {
        if (page === WizardPage.First) {
            setPage(WizardPage.Second);
        } else if (page === WizardPage.Second) {
            createMeeting.mutate(
                {
                    eventId,
                    req: {
                        locationType,
                        selectedTimes: selectedTimes.slice(0, LIMIT_OF_TIMES_TO_SEND),
                        members: [{ userProfileId: otherAttendeeId, required: true }],
                        intro: introMessage,
                        physicalLocation: physicalLocationText,
                    },
                },
                {
                    onSuccess: () => {
                        setView(View.Manage);
                    },
                    onError: () => {
                        setIsTimesNotFoundError(true);
                    },
                }
            );
            const durationInMinutes = timesResponse?.duration / (1000 * 60);
            trackClickEvent({
                subCategory: 'MEETING_INVITATION_SENT',
                description: {
                    location_type: locationType,
                    meeting_length: durationInMinutes,
                    number_of_invitees: 1,
                    invitation_text: introMessage,
                },
            });
        }
    };

    const onClickBack = () => {
        setPage(WizardPage.First);
    };

    const setLocationTypeHandle = (locationType: LocationType) => {
        setLocationType(locationType);
        trackClickEvent({
            subCategory: 'SCHEDULED_MEETING_TYPE_CHOSEN',
            description: {
                meeting_location: locationType === LocationType.Physical ? 'IN-PERSON' : 'VIRTUAL',
            },
        });
    };

    const onCloseHandle = () => {
        setView(View.Manage);
        trackClickEvent({
            subCategory: 'CLOSE_SCHEDULE_MEETING_FLOW',
        });
    };

    const blankLocation =
        page === WizardPage.First &&
        locationType === LocationType.Physical &&
        !physicalLocationText.trim();

    const disableForwardButton =
        (page === WizardPage.Second && !selectedTimes.length) || blankLocation;

    const noAvailableTimes = isTimesNotFoundError || timesResponse?.times.length < 1;

    const isBackBtnShow = page === WizardPage.Second && !noAvailableTimes;

    return (
        <Track>
            <SchedulingOverlayWrapper
                isBackBtnShow={isBackBtnShow}
                onClose={onCloseHandle}
                onClickBack={onClickBack}
            >
                {isLoadingTimes ? (
                    <WizardLoader />
                ) : noAvailableTimes ? (
                    <ZeroAvailableTimes
                        otherAttendee={otherAttendee}
                        refetchScheduledMeetingTimes={onRefetch}
                    />
                ) : (
                    <div className={classes.root}>
                        {page === WizardPage.First && (
                            <FirstPage
                                introMessage={introMessage}
                                setIntroMessage={setIntroMessage}
                                locationType={locationType}
                                setLocationType={setLocationTypeHandle}
                                physicalLocationText={physicalLocationText}
                                setPhysicalLocationText={setPhysicalLocationText}
                                nameToDisplay={nameToDisplay}
                            />
                        )}
                        {page === WizardPage.Second && (
                            <SecondPage
                                times={timesResponse?.times}
                                timeZone={timeZoneId}
                                selectedTimes={selectedTimes}
                                meetingDuration={timesResponse?.duration}
                                onTimePickerChange={times =>
                                    dispatchSelectedTime({ type: 'toggle', times })
                                }
                                onTimePickerSelect={times =>
                                    dispatchSelectedTime({ type: 'select', times })
                                }
                                onTimePickerDeselect={times =>
                                    dispatchSelectedTime({ type: 'deselect', times })
                                }
                                onTimePickerLoadMore={times => {
                                    // select all newly shown times
                                    dispatchSelectedTime({ type: 'select', times });
                                }}
                            />
                        )}

                        <div className={classes.forwardButtonWrapper}>
                            <LoaderButton
                                variant="fillPrimary"
                                icon={<ArrowRightSLine />}
                                className={classes.forwardButton}
                                onClick={onClickForward}
                                disabled={disableForwardButton}
                                isLoading={createMeeting.isLoading}
                            >
                                {page === WizardPage.First
                                    ? 'See available times'
                                    : 'Send meeting request'}
                            </LoaderButton>
                        </div>
                    </div>
                )}
            </SchedulingOverlayWrapper>
        </Track>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        root: {
            ...theme.typography.t60,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%',
        },
        forwardButtonWrapper: {
            marginTop: 'auto',
        },
        forwardButton: {
            ...theme.typography.t60,
            width: '100%',
            padding: 7,
            minHeight: 48,
        },
    }),
    { classNamePrefix: 'meetings-scheduling-wizard' }
);

export default SchedulingWizard;
